import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTourEngine } from '../engine/useTourEngine'
import { resolveTourForRoute } from '../registry/tour-registry'
import { useTourProgressStore } from '../stores/tour-progress.store'
import { buildTourContext } from './useTourContext'
import { AI_PROMPT_QUERY_PARAM } from '../config/tour.config'

// UI state shared across components (module-level singletons).
const explorePanelOpen = ref(false)

/**
 * Public API for the app: start/resume the current screen's tour, toggle training
 * mode, open Exploration mode, and hand a task to the AI assistant.
 */
export function useTour() {
  const route = useRoute()
  const router = useRouter()
  const engine = useTourEngine()
  const progress = useTourProgressStore()

  /** The tour bound to the current route (if available to this user). */
  const currentTour = computed(() =>
    resolveTourForRoute(route.name as string | undefined, buildTourContext()),
  )
  const hasCurrentTour = computed(() => currentTour.value !== null)

  function startCurrentScreen(): void {
    if (!currentTour.value) return
    // Replay the whole screen: start the matching tour now and let this route's
    // other state tours (e.g. List Builder results) auto-appear again this session.
    engine.replayForRoute(route.name as string | undefined)
  }

  function resumeCurrentScreen(): void {
    const tour = currentTour.value
    if (!tour) return
    if (progress.isCompleted(tour.id) || progress.lastStepIndex(tour.id) === 0) {
      engine.startTour(tour)
    } else {
      engine.resumeTour(tour)
    }
  }

  /** Turn "training mode on all screens" on/off (controls first-visit auto-start). */
  function setTrainingModeAllScreens(enabled: boolean): void {
    progress.trainingModeAllScreens = enabled
  }

  /**
   * "Turn off training mode" from inside a running tour: tours stop opening on their own on every
   * screen, and the current one closes. The help button still starts a screen's tour on demand and
   * can switch training mode back on.
   */
  function disableTrainingMode(): void {
    progress.trainingModeAllScreens = false
    if (engine.isRunning.value) engine.close()
  }

  function openExplore(): void {
    explorePanelOpen.value = true
  }
  function closeExplore(): void {
    explorePanelOpen.value = false
  }

  /** Hand a task to the agentic AI assistant, seeding an initial prompt. */
  function executeWithAI(prompt: string): void {
    void router.push({ name: 'AiAssistant', query: { [AI_PROMPT_QUERY_PARAM]: prompt } })
  }

  return {
    ...engine,
    currentTour,
    hasCurrentTour,
    explorePanelOpen,
    startCurrentScreen,
    resumeCurrentScreen,
    setTrainingModeAllScreens,
    disableTrainingMode,
    openExplore,
    closeExplore,
    executeWithAI,
  }
}
